/** @module Registration with an invite link: who invited, name, e-mail, password. */
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { User } from "lucide-react";
import { type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError, problemFrom } from "@/api/client";
import { AuthLayout, PasswordInput } from "@/features/auth/auth-layout";
import { day } from "@/lib/format";
import { signIn } from "@/lib/session";

async function fetchInvite(token: string) {
  const { data, response } = await api.GET("/api/v1/auth/invites/{invite_token}", {
    params: { path: { invite_token: token } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

export function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { invite = "" } = useSearch({ strict: false });
  const preview = useQuery({
    queryKey: ["invite", invite],
    queryFn: () => fetchInvite(invite),
    enabled: invite.length > 0,
    retry: false,
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data, response } = await api.POST("/api/v1/auth/register", {
        body: { invite_token: invite, email, password, display_name: name.trim() },
      });
      if (!data) throw problemFrom(response);
      // The account exists; signing in right away spares typing it all again.
      await signIn(email, password, true);
      await navigate({ to: "/" });
    } catch (cause) {
      const code = cause instanceof ApiError ? cause.code : "unexpected_response";
      setError(t(`auth.error.${code}`, { defaultValue: t("auth.error.generic") }));
    } finally {
      setBusy(false);
    }
  }

  const invalid = !invite || preview.isError;
  return (
    <AuthLayout
      title={t("auth.register.title")}
      lead={t("auth.register.lead")}
      badge={
        preview.data?.inviter ? (
          <span className="badge badge-accent self-start">
            <User />
            {t("auth.register.invitedBy", { name: preview.data.inviter })}
          </span>
        ) : null
      }
      footer={
        preview.data
          ? t("auth.register.validUntil", {
              date: day(preview.data.expires_at, i18n.resolvedLanguage ?? "de"),
            })
          : null
      }
    >
      {invalid ? (
        <p role="alert" className="callout callout-warn">
          {t("auth.error.invite_invalid")}
        </p>
      ) : (
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <label className="field">
            <span className="field-label">{t("auth.register.name")}</span>
            <input
              required
              maxLength={80}
              autoComplete="nickname"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              className="input"
            />
          </label>
          <label className="field">
            <span className="field-label">{t("auth.email")}</span>
            <input
              type="email"
              required
              autoComplete="username"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              className="input"
            />
          </label>
          <div className="field">
            <label htmlFor="password" className="field-label">
              {t("auth.password")}
            </label>
            <PasswordInput
              id="password"
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              describedBy="password-hint"
            />
            <span id="password-hint" className="field-hint">
              {t("auth.register.passwordHint")}
            </span>
          </div>
          {error ? (
            <p role="alert" className="callout callout-warn">
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={busy || preview.isPending}
            className="btn btn-primary btn-block"
          >
            {t("auth.register.submit")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
